import React, { useRef, useState, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { heroVideo, heroVideoWebm, heroPoster } from '../mock';

// Background hero video with autoplay (muted) and a mute/unmute toggle.
const HeroVideo = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    v.muted = true;            // ensure DOM property is set (React doesn't always reflect it)
    v.defaultMuted = true;
    const tryPlay = () => v.play().catch(() => {});
    v.load();                  // force the browser to begin buffering
    tryPlay();
    v.addEventListener('loadeddata', tryPlay);
    v.addEventListener('canplay', tryPlay);
    return () => {
      v.removeEventListener('loadeddata', tryPlay);
      v.removeEventListener('canplay', tryPlay);
    };
    // videoRef is a stable ref; this effect intentionally runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    v.play().catch(() => {});
  };

  return (
    <>
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full object-cover"
        poster={heroPoster}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        data-testid="hero-video"
      >
        <source src={heroVideoWebm} type="video/webm" />
        <source src={heroVideo} type="video/mp4" />
      </video>
      {/* Left-weighted overlay: keeps headline readable while letting the video show on the right */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(90deg, rgba(10,14,22,0.88) 0%, rgba(10,14,22,0.60) 38%, rgba(10,14,22,0.22) 70%, rgba(10,14,22,0.38) 100%)' }}
      />
      {/* Gentle bottom fade to blend into the next section */}
      <div
        className="absolute inset-x-0 bottom-0 h-1/3"
        style={{ background: 'linear-gradient(180deg, rgba(10,14,22,0) 0%, #0a0e16 100%)' }}
      />
      <button
        onClick={toggleMute}
        aria-label={muted ? 'Turn on sound' : 'Mute video'}
        data-testid="hero-video-sound-toggle"
        className="absolute bottom-24 right-6 z-20 inline-flex items-center gap-2 rounded-full border border-white/40 bg-[#0a0e16]/75 px-4 py-2.5 text-sm font-semibold text-white shadow-lg backdrop-blur-md transition-colors hover:border-[#e8743b] hover:bg-[#e8743b]"
      >
        {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        <span>{muted ? 'Tap for sound' : 'Mute'}</span>
      </button>
    </>
  );
};

export default HeroVideo;
